import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, ShoppingCart, Newspaper, Briefcase, Image as ImageIcon, MessageSquare, Users, LogOut, ChevronLeft, Magnet, KeyRound, Receipt, Truck, IdCard } from "lucide-react";
import { Toaster } from "sonner";

const LINKS = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/purchases", label: "Purchases", icon: Receipt },
    { to: "/admin/suppliers", label: "Suppliers", icon: Truck },
    { to: "/admin/employees", label: "Employees", icon: IdCard },
    { to: "/admin/news", label: "News", icon: Newspaper },
    { to: "/admin/careers", label: "Careers", icon: Briefcase },
    { to: "/admin/gallery", label: "Gallery & banners", icon: ImageIcon },
    { to: "/admin/messages", label: "Messages", icon: MessageSquare },
    { to: "/admin/leads", label: "Leads", icon: Magnet },
    { to: "/admin/users", label: "Users", icon: Users },
];

export default function AdminLayout() {
    const { user, logout, checked } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (checked && (!user || user.role !== "admin")) nav("/login");
    }, [checked, user, nav]);

    if (!user || user.role !== "admin") return null;

    return (
        <div className="min-h-screen flex bg-surface" data-testid="admin-layout">
            <aside className="w-60 bg-ink text-white flex flex-col">
                <div className="p-6 border-b border-white/10">
                    <Link to="/" className="flex items-center gap-2.5">
                        <img src="/logo.png" alt="Peppa Target" className="h-10 w-auto bg-white rounded p-0.5" />
                        <div>
                            <div className="font-display font-bold text-sm">Peppa Admin</div>
                            <div className="text-[10px] uppercase tracking-wider text-white/50">{user.name}</div>
                        </div>
                    </Link>
                </div>
                <nav className="flex-1 p-3 space-y-0.5">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                                    isActive ? "bg-primary text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                                }`
                            }
                            data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                            <l.icon className="w-4 h-4" />
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <Link to="/profile" className="flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-white" data-testid="admin-change-password-link">
                        <KeyRound className="w-3 h-3" /> My profile / password
                    </Link>
                    <Link to="/" className="flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-white">
                        <ChevronLeft className="w-3 h-3" /> Back to site
                    </Link>
                    <button onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/60 hover:text-white" data-testid="admin-logout">
                        <LogOut className="w-3 h-3" /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 overflow-x-auto">
                <Outlet />
            </main>
            <Toaster position="top-right" richColors />
        </div>
    );
}
